/**
 * Context compound tool — gather, build.
 * Consolidates gather-context and get-build-context into one tool.
 * gather: entry-based graph traversal, task-based auto-load, or graph mode with provenance.
 * build: structured build spec for an entry (requires active session).
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { kernelQuery, kernelMutation, getWorkspaceId, requireActiveSession, runWithToolContext } from "../client.js";
import { success, failure, thinWrapper, parseOrFail, notFoundResult, validationResult, type ToolResult } from "../envelope.js";
import { translateStaleToolNames } from "./knowledge-helpers.js";
import { trackKnowledgeGap } from "../analytics.js";
import { recordGap } from "../gap-store.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";
import { handleGetLastVerifiedBrief } from "./documents.js";

function annotateStaleNames(text: string): string {
  const footnote = translateStaleToolNames(text);
  return footnote ? text + footnote : text;
}

/**
 * Collection header hint for epistemic collections in context views.
 * BET-281: When governanceRole field is available on collections, replace
 * name-based checks with governanceRole === 'epistemic'. Currently the
 * collection display name is stable and this is pure UX chrome.
 */
function epistemicCollectionHint(collectionName: string): string {
  const lc = collectionName.toLowerCase();
  if (lc === "insights") return " — _verify confidence with `entries action=get`_";
  if (lc === "assumptions") return " — _check evidence strength with `entries action=get`_";
  return "";
}

interface ContextEntry {
  entryId?: string;
  name: string;
  collectionName: string;
  relationType: string;
  relationDirection: "outgoing" | "incoming";
  hop: number;
  score?: number;
  scoreFlags?: string[];
  preview?: string;
  /** BET-240 S0: Entry provenance — human, agent, or external. */
  origin?: "human" | "agent" | "external";
  provenance?: Array<{ entryId?: string; name: string; relationType: string }>;
}

interface GatherResult {
  root: { entryId: string; name: string } | null;
  related: ContextEntry[];
  totalRelations: number;
  hopsTraversed: number;
}

interface GraphGatherResult {
  root: { entryId: string; name: string } | null;
  context: ContextEntry[];
  totalFound: number;
  hopsTraversed: number;
}

interface TaskGatherResult {
  groundingStatus: "grounded" | "partial" | "missing";
  totalFound: number;
  confidence: string;
  seeds?: Array<{ entryId?: string | null; name: string; source?: "vector" | "fts" | "both" }>;
  bindingGovernance: {
    principles: Array<{ entryId: string | null; name: string; collectionName?: string | null; collectionSlug?: string | null; score?: number; scoreFlags?: string[]; preview?: string; origin?: "human" | "agent" | "external"; tier?: number }>;
    standards: Array<{ entryId: string | null; name: string; collectionName?: string | null; collectionSlug?: string | null; score?: number; scoreFlags?: string[]; preview?: string; origin?: "human" | "agent" | "external"; tier?: number }>;
    businessRules: Array<{ entryId: string | null; name: string; collectionName?: string | null; collectionSlug?: string | null; score?: number; scoreFlags?: string[]; preview?: string; origin?: "human" | "agent" | "external"; tier?: number }>;
    byCollection?: Array<{
      collectionSlug: string | null;
      collectionName: string | null;
      canonicalKey: string | null;
      entries: Array<{ entryId: string | null; name: string; collectionName?: string | null; collectionSlug?: string | null; score?: number; scoreFlags?: string[]; preview?: string; origin?: "human" | "agent" | "external"; tier?: number }>;
    }>;
  };
  steeringContext: ContextEntry[];
  supportingContext: ContextEntry[];
}

type BindingGovernanceEntry = TaskGatherResult["bindingGovernance"]["principles"][number];

interface JourneyStage {
  slotId: string;
  label: string;
  ingredients: Array<{ entryId: string; name: string; status: string }>;
}

interface JourneyGatherResult {
  map: { entryId: string; name: string; templateId: string; templateName: string };
  stages: JourneyStage[];
  context: ContextEntry[];
  totalFound: number;
  seedCount: number;
  truncated: boolean;
}

interface BuildResult {
  entry: { name: string; entryId?: string; collectionSlug: string; status: string; data: Record<string, unknown> };
  relatedByCollection: Record<string, Array<{ entryId?: string; name: string; relationType: string }>>;
  businessRules: Array<{ entryId?: string; name: string; description?: string }>;
  glossaryTerms: Array<{ entryId?: string; name: string; definition?: string }>;
  rulesToHonor: unknown[];
  chainRefs: string[];
  contextTruncated: boolean;
}

interface NeighborhoodItem {
  entryDocId: string;
  entryId: string | null;
  name: string;
  status?: string;
  hop?: number;
  relationType?: string;
  unresolvedFlag?: boolean;
  openCount?: number;
  blockedCount?: number;
}

interface NeighborhoodResult {
  blockingChain: NeighborhoodItem[];
  dependencyChain: NeighborhoodItem[];
  parentContext: (NeighborhoodItem & { openCount: number; blockedCount: number }) | null;
  relatedTensions: NeighborhoodItem[];
  stalenessSignal: { isStale: boolean; daysSinceUpdate: number } | null;
  isolationSignal: boolean;
}

// WP-484 S2 (§3): `context` absorbs `documents` → action=last-verified-brief.
// Legacy `documents` tool stays registered alongside (§7 "alongside"
// mechanic) — both call the SAME handleGetLastVerifiedBrief function, one
// business-logic path.
const CONTEXT_ACTIONS = ["gather", "build", "neighborhood", "changes", "chain", "cross-cut", "incremental", "brief", "last-verified-brief"] as const;
type ContextAction = (typeof CONTEXT_ACTIONS)[number];

// §3a — the REGISTERED layer: flat ZodObject, action enum + superset of params.
export const contextSchema = z.object({
  // provenance: neighborhood BET-142; changes/chain/cross-cut/incremental/brief BET-239 (E4, E6)
  action: z.enum(CONTEXT_ACTIONS).describe(
    "'gather': assemble knowledge context (entry graph, task auto-load, journey mode, or graph mode). " +
    "'build': structured build spec for an entry. " +
    "'neighborhood': typed graph neighborhood for an entry — blocking chain, dependencies, parent context, tensions, staleness. " +
    "'changes': entries modified and relations created since a timestamp. Requires 'since' parameter. " +
    "'chain': directed traversal along one relation type to depth 4. Requires entryId. Optional: direction, relationType, maxHops (1-4). " +
    "'cross-cut': structural aggregation — all relations of a given type grouped by source collection. Requires 'relationType' parameter. " +
    "'incremental': delta since last brief run for a skill. Requires 'skill' parameter. Returns only entries changed since the skill's last brief. " +
    "'brief': compound intelligence query. Requires 'briefType' parameter: 'steering' (changes + structure + delta + readiness), 'confidence' (changes + active bets + tensions), or 'delta' (changes + relations since timestamp). Optional 'since' for delta type. " +
    "'last-verified-brief': fetch the most recent verified brief snapshot for a (templateId, scopeKey) pair (absorbs documents action=get-last-verified-brief). Requires templateId and scopeKey.",
  ),
  entryId: z.string().max(200).optional().describe("For 'build'/'neighborhood'/'chain': entry ID, e.g. '<PREFIX>-<n>'. For 'gather': optional entry ID for entry-graph mode."),
  mapEntryId: z.string().max(200).optional().describe(
    "For 'gather': journey map entry ID for journey-aware context. Returns context organised by journey stage. " +
    "Takes precedence over entryId when both are supplied. Example: '<PREFIX>-<n>'.",
  ),
  task: z.string().max(2000).optional().describe("For 'gather': natural-language task description for loading task-relevant governance, binding constraints, and supporting context."),
  since: z.string().max(200).optional().describe(
    "For 'changes': ISO 8601 timestamp — returns entries/relations modified since this time. " +
    "For 'brief' briefType='delta': optional custom timestamp. Example: '2026-03-24T00:00:00Z'.",
  ),
  direction: z.enum(["outgoing", "incoming"]).default("outgoing").optional()
    .describe("For 'chain' action: traversal direction. 'outgoing' follows relations from source, 'incoming' follows relations to source. Default: outgoing."),
  relationType: z.string().max(200).optional().describe(
    "Relation type filter. For 'chain': optional filter to traverse only this relation type. " +
    "For 'cross-cut': required — scans all relations of this type across the workspace. " +
    "Examples: 'part_of', 'informs', 'governs', 'blocks', 'depends_on'.",
  ),
  mode: z.enum(["search", "graph"]).default("search").optional()
    .describe("For gather: 'search' (default) or 'graph' (enhanced with provenance paths). Ignored when mapEntryId is provided."),
  maxHops: z.number().min(1).max(4).default(2)
    .describe("Relation traversal depth (1=direct only, 2=default, 3=wide net, 4=deep chain walk)"),
  maxResults: z.number().min(1).max(25).default(10).optional()
    .describe("Max entries to return in gather task mode (default 10)"),
  strategy: z.enum(["hybrid", "keyword"]).default("keyword").optional()
    .describe("Seed strategy for task-based gather: 'keyword' (FTS only, default) or 'hybrid' (vector + FTS). Only affects task mode."),
  skill: z.string().max(200).optional().describe(
    "Skill name for 'incremental' action — identifies which skill's brief history to compare against. " +
    "Examples: 'preflight', 'shaping', 'review'. Required when action is 'incremental'.",
  ),
  briefType: z.enum(["steering", "confidence", "delta"]).optional().describe(
    "Compound query type for 'brief' action. " +
    "'steering': 7d changes + structural aggregation (part_of, depends_on, constrains) + incremental delta + workspace readiness. " +
    "'confidence': 7d changes + active bets summary + active tensions breakdown. " +
    "'delta': changes + relations since a custom timestamp (use 'since' param). " +
    "Required when action is 'brief'.",
  ),
  templateId: z.string().max(200).optional().describe(
    "For 'last-verified-brief': brief template identifier — currently 'steering-brief' is the only registered template.",
  ),
  scopeKey: z.string().max(200).optional().describe(
    "For 'last-verified-brief': canonical scope key — 'workspace:<workspaceId>' or 'initiative:<INI-ID>'.",
  ),
});

// §3b — the HANDLER layer: per-action discriminatedUnion.
const contextGatherVariant = z.object({
  action: z.literal("gather"),
  entryId: z.string().max(200).optional(),
  mapEntryId: z.string().max(200).optional(),
  task: z.string().max(2000).optional(),
  mode: z.enum(["search", "graph"]).optional().default("search"),
  maxHops: z.number().min(1).max(4).optional().default(2),
  maxResults: z.number().min(1).max(25).optional().default(10),
  strategy: z.enum(["hybrid", "keyword"]).optional().default("keyword"),
});
const contextBuildVariant = z.object({
  action: z.literal("build"),
  entryId: z.string().max(200),
  maxHops: z.number().min(1).max(4).optional().default(2),
});
const contextNeighborhoodVariant = z.object({
  action: z.literal("neighborhood"),
  entryId: z.string().max(200),
});
const contextChangesVariant = z.object({
  action: z.literal("changes"),
  since: z.string().max(200),
});
const contextChainVariant = z.object({
  action: z.literal("chain"),
  entryId: z.string().max(200),
  direction: z.enum(["outgoing", "incoming"]).optional().default("outgoing"),
  maxHops: z.number().min(1).max(4).optional().default(2),
  relationType: z.string().max(200).optional(),
});
const contextCrossCutVariant = z.object({
  action: z.literal("cross-cut"),
  relationType: z.string().max(200),
});
const contextIncrementalVariant = z.object({
  action: z.literal("incremental"),
  skill: z.string().max(200),
});
const contextBriefVariant = z.object({
  action: z.literal("brief"),
  briefType: z.enum(["steering", "confidence", "delta"]),
  since: z.string().max(200).optional(),
});
const contextLastVerifiedBriefVariant = z.object({
  action: z.literal("last-verified-brief"),
  templateId: z.string().max(200),
  scopeKey: z.string().max(200),
});

export const contextActionUnion = z.discriminatedUnion("action", [
  contextGatherVariant,
  contextBuildVariant,
  contextNeighborhoodVariant,
  contextChangesVariant,
  contextChainVariant,
  contextCrossCutVariant,
  contextIncrementalVariant,
  contextBriefVariant,
  contextLastVerifiedBriefVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const CONTEXT_ACTION_SPECS: Record<ContextAction, ActionParamSpec> = {
  gather: { params: ["entryId", "mapEntryId", "task", "mode", "maxHops", "maxResults", "strategy"], description: "All params optional; entryId/mapEntryId/task select the mode." },
  build: { params: ["entryId", "maxHops"], description: "entryId is required." },
  neighborhood: { params: ["entryId"], description: "entryId is required." },
  changes: { params: ["since"], description: "since (ISO 8601) is required." },
  chain: { params: ["entryId", "direction", "maxHops", "relationType"], description: "entryId is required." },
  "cross-cut": { params: ["relationType"], description: "relationType is required." },
  incremental: { params: ["skill"], description: "skill is required." },
  brief: { params: ["briefType", "since"], description: "briefType is required." },
  "last-verified-brief": { params: ["templateId", "scopeKey"], description: "Both templateId and scopeKey are required." },
};

export function registerContextTools(server: McpServer): void {
  // §3b handler map — one entry per action, dispatched via dispatchDiscriminated.
  const contextHandlers: Record<ContextAction, (data: any) => Promise<ToolResult>> = {
    gather: (data) => data.mapEntryId
      ? handleJourneyGather(server, data.mapEntryId, data.maxHops ?? 2, data.maxResults ?? 20)
      : handleGather(server, data.entryId, data.task, data.mode ?? "search", data.maxHops ?? 2, data.maxResults ?? 10, data.strategy ?? "keyword"),
    build: (data) => handleBuild(server, data.entryId, data.maxHops ?? 2),
    neighborhood: (data) => handleNeighborhood(data.entryId),
    changes: (data) => handleChanges(data.since),
    chain: (data) => handleChainWalk(data.entryId, data.direction ?? "outgoing", data.maxHops ?? 2, data.relationType),
    "cross-cut": (data) => handleCrossCut(data.relationType),
    incremental: (data) => handleIncremental(data.skill),
    brief: (data) => {
      const sinceMs = data.briefType === "delta" && data.since ? Date.parse(data.since) : undefined;
      if (data.briefType === "delta" && data.since && (sinceMs === undefined || isNaN(sinceMs))) {
        return Promise.resolve({
          content: [{ type: "text" as const, text: `Invalid 'since' timestamp: '${data.since}'. Use ISO 8601 format (e.g. '2026-03-24T00:00:00Z').` }],
          structuredContent: failure("VALIDATION_ERROR", `Invalid 'since' timestamp: '${data.since}'.`),
        });
      }
      return handleBrief(data.briefType, sinceMs);
    },
    "last-verified-brief": (data) => handleGetLastVerifiedBrief(data.templateId, data.scopeKey),
  };

  server.registerTool(
    "context",
    {
      title: "Context",
      description:
        "Assemble knowledge context in one call. Nine actions:\n\n" +
        "- **gather**: Three modes — (1) By entry: traverse the graph around a specific entry. " +
        "(2) By task: load task-relevant governance, binding constraints, and supporting domain knowledge for a natural-language task. " +
        "(3) Graph mode (entryId + mode='graph'): enhanced traversal with provenance paths.\n" +
        "- **build**: Structured build spec for any entry — data, related entries, business rules, " +
        "glossary terms, chain refs. Use when starting a build. Requires active session.\n" +
        "- **neighborhood**: Typed graph neighborhood — blocking chain (what this blocks, 3 hops), " +
        "dependency chain (what blocks this), parent context (sibling count), related tensions, " +
        "staleness signal, isolation signal. Fast indexed lookups. Requires entryId.\n" +
        "- **changes**: Detect entries modified and relations created since a timestamp. " +
        "Use for incremental updates instead of full traversal. Requires 'since' (ISO 8601). " +
        // BET-239
        "Returns grouped counts by collection. Cap: 200 entries.\n" +
        // BET-239
        "- **chain**: Directed deep traversal along one relation type to depth 4. " +
        "Use for dependency analysis, governance chains, strategic alignment. Requires entryId. " +
        "Optional: direction (outgoing/incoming), relationType, maxHops (1-4). " +
        "Cycle detection, fan-out cap 10/hop, total cap 100 nodes. Returns tree + edges.\n" +
        "- **cross-cut**: Structural aggregation — all relations of a given type across the workspace, " +
        "grouped by source collection. Use to answer 'show me everything connected by part_of'. " +
        // BET-239
        "Requires 'relationType'. Cap: 500 entries.\n" +
        "- **incremental**: Delta since last brief run for a skill. Returns only entries changed since " +
        "the skill's last brief, filtering out previously surfaced entries. Requires 'skill' parameter. " +
        // BET-239 E4
        "Use for steering briefs that need incremental awareness.\n" +
        "- **brief**: Compound intelligence query composing multiple primitives. Requires 'briefType': " +
        "'steering' (7d changes + structure + delta + readiness), 'confidence' (7d changes + active bets + tensions), " +
        // BET-239 E6
        "or 'delta' (changes since custom timestamp). Auto-records brief run for steering.\n" +
        "- **last-verified-brief**: Fetch the most recent verified brief snapshot for a (templateId, scopeKey) pair " +
        "(absorbs `documents`). Use to build delta narratives.",
      inputSchema: contextSchema,
      annotations: { readOnlyHint: true, idempotentHint: true, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(contextSchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "context", action }, () =>
        dispatchDiscriminated("context", contextActionUnion, parsed.data, CONTEXT_ACTION_SPECS, contextHandlers),
      );
    }),
  );
}

async function handleGather(
  server: McpServer,
  entryId: string | undefined,
  task: string | undefined,
  mode: "search" | "graph",
  maxHops: number,
  maxResults: number,
  strategy: "hybrid" | "keyword" = "keyword",
) {
  if (!entryId && !task) {
    return validationResult("Provide either entryId (graph traversal) or task (auto-load context).");
  }

  // Graph mode: enhanced traversal with provenance paths
  if (entryId && mode === "graph") {
    await server.sendLoggingMessage({
      level: "info",
      data: `Graph traversal for ${entryId} (depth: ${maxHops})...`,
      logger: "product-brain",
    });

    const result = await kernelQuery<GraphGatherResult>("chain.graphGatherContext", {
      entryId,
      maxHops,
    });

    if (!result?.root) {
      return notFoundResult(entryId!, `Entry '${entryId}' not found. Try search to find the right ID.`);
    }
    const root = result.root;

    if (result.context.length === 0) {
      const recorded = recordGap({ query: root.entryId, tool: "context", action: "gather", gapType: "context_graph_empty" });
      if (recorded) {
        getWorkspaceId()
          .then((wsId) => trackKnowledgeGap(wsId, { query: root.entryId, tool: "context", action: "gather", gap_type: "context_graph_empty" }))
          .catch(() => {});
      }

      return {
        content: [{
          type: "text" as const,
          text: `# Context for ${root.entryId}: ${root.name}\n\n` +
            `_No relations found._ This entry is not yet connected to the knowledge graph.\n\n` +
            `Use \`relations action=suggest\` to discover potential connections.`,
        }],
        structuredContent: success(
          `No relations found for ${root.entryId}. Entry not yet connected.`,
          { root, context: [], totalFound: 0 },
          [{ tool: "relations", description: "Discover connections", parameters: { action: "suggest", entryId: root.entryId } }],
        ),
      };
    }

    const byCollection = new Map<string, ContextEntry[]>();
    for (const entry of result.context) {
      const key = entry.collectionName;
      if (!byCollection.has(key)) byCollection.set(key, []);
      byCollection.get(key)!.push(entry);
    }

    const lines: string[] = [
      `# Context for ${root.entryId}: ${root.name} (graph mode)`,
      `_${result.totalFound} related entries across ${byCollection.size} collections (${result.hopsTraversed} hops)_`,
      "",
    ];

    for (const [collName, entries] of byCollection) {
      lines.push(`## ${collName} (${entries.length})${epistemicCollectionHint(collName)}`);
      for (const e of entries) {
        const arrow = e.relationDirection === "outgoing" ? "→" : "←";
        const id = e.entryId ? `${e.entryId}: ` : "";
        const hopLabel = e.hop > 1 ? ` (hop ${e.hop})` : "";
        const scoreLabel = typeof e.score === "number" ? `  ${e.score}` : "";
        const flagsLabel = e.scoreFlags?.length ? ` [${e.scoreFlags.join(", ")}]` : "";
        const originLabel = e.origin ? ` (origin: ${e.origin})` : "";
        const provenancePath = e.provenance && e.provenance.length > 1
          ? `\n  _Path: ${e.provenance.map((p) => `${p.entryId ?? p.name} [${p.relationType}]`).join(" → ")}_`
          : "";
        const preview = e.preview ? `\n  ${e.preview.substring(0, 120)}` : "";
        lines.push(`- ${arrow} **${e.relationType}** ${id}${e.name}${hopLabel}${scoreLabel}${flagsLabel}${originLabel}${provenancePath}${preview}`);
      }
      lines.push("");
    }

    const rootId = root.entryId ?? "root";
    const nodes = [
      { id: rootId, name: root.name, collectionName: "" },
      ...result.context.map((e) => ({
        id: e.entryId ?? String(Math.random()),
        name: e.name,
        collectionName: e.collectionName,
      })),
    ];
    const edges = result.context
      .filter((e) => e.entryId)
      .map((e) => ({
        source: rootId,
        target: e.entryId!,
        type: e.relationType,
      }));

    return {
      content: [{ type: "text" as const, text: lines.join("\n") }],
      structuredContent: success(
        `Gathered ${result.totalFound} related entries for ${root.entryId} (${result.hopsTraversed} hops, graph mode).`,
        { nodes, edges, root, totalFound: result.totalFound },
      ),
    };
  }

  // Task mode: unified search-then-traverse via intelligence layer
  // BET-205 Slice 2: strategy routing — hybrid calls vector+FTS action, keyword uses existing FTS query
  if (task && !entryId) {
    const strategyLabel = strategy === "hybrid" ? "hybrid (vector + FTS)" : "keyword (FTS only)";
    await server.sendLoggingMessage({
      level: "info",
      data: `Loading context for task: "${task.substring(0, 80)}..." [strategy: ${strategyLabel}]`,
      logger: "product-brain",
    });

    const result = strategy === "hybrid"
      ? await kernelQuery<TaskGatherResult>("chain.resolveTaskStartupHybrid", {
          task,
          maxHops,
          maxResults,
        })
      : await kernelQuery<TaskGatherResult>("chain.resolveTaskStartup", {
          task,
          maxHops,
          maxResults,
        });

    if (!result || result.totalFound === 0) {
      const taskQuery = task.slice(0, 200);
      const recorded = recordGap({ query: taskQuery, tool: "context", action: "gather", gapType: "context_task_empty" });
      if (recorded) {
        getWorkspaceId()
          .then((wsId) => trackKnowledgeGap(wsId, { query: taskQuery, tool: "context", action: "gather", gap_type: "context_task_empty" }))
          .catch(() => {});
      }

      return {
        content: [{
          type: "text" as const,
          text: `# Task Constraints Loaded\n\n**Confidence:** None\n\n` +
            `No task-relevant governance or supporting context was found yet. If you discover relevant knowledge during this task, ` +
            `use \`capture\` with a name and description to add it.\n\n` +
            `_This helps future sessions start with better constraints and context._`,
        }],
        structuredContent: failure(
          "NOT_FOUND",
          "No task-relevant governance or supporting context was found yet.",
          "If you discover relevant knowledge during this task, capture it with a name and description.",
          [{ tool: "entries", description: "Search for related terms", parameters: { action: "search", query: task.split(" ").slice(0, 3).join(" ") } }],
        ),
      };
    }

    const bindingGovernanceCollections = result.bindingGovernance.byCollection ?? [];
    const hasBindingGovernance =
      bindingGovernanceCollections.some((bucket) => bucket.entries.length > 0) ||
      result.bindingGovernance.principles.length > 0 ||
      result.bindingGovernance.standards.length > 0 ||
      result.bindingGovernance.businessRules.length > 0;

    const byCollection = new Map<string, ContextEntry[]>();
    for (const entry of result.supportingContext) {
      const key = entry.collectionName;
      if (!byCollection.has(key)) byCollection.set(key, []);
      byCollection.get(key)!.push(entry);
    }
    const steeringCount = result.steeringContext.length;
    const collectionCount = new Set(
      [...result.steeringContext, ...result.supportingContext].map((entry) => entry.collectionName),
    ).size;

    // Format seed display — hybrid seeds include source (vector/fts/both), keyword seeds do not
    const seedDisplay = result.seeds?.map((s: any) => {
      const id = s.entryId ?? s.name;
      return s.source ? `${id} [${s.source}]` : id;
    }).join(", ") ?? "none";

    const lines: string[] = [
      `# Task Constraints Loaded`,
      `**Confidence:** ${result.confidence.charAt(0).toUpperCase() + result.confidence.slice(1)}`,
      `**Grounding:** ${result.groundingStatus}`,
      `**Strategy:** ${strategyLabel}`,
      `**Matched:** ${result.totalFound} entries across ${collectionCount} collection${collectionCount === 1 ? "" : "s"}`,
      `**Seeds:** ${seedDisplay}`,
      "",
    ];

    lines.push("_Use this task gather as the live constraint load for the work you are about to do, then open any cited entries for full details._");
    lines.push("");

    if (hasBindingGovernance) {
      lines.push("## Binding governance");
      lines.push("_These are the non-optional rules and standards that govern this task._");
      lines.push("");

      const renderGovernanceEntries = (entries: BindingGovernanceEntry[]) => {
        for (const entry of entries) {
          const id = entry.entryId ? `**${entry.entryId}:** ` : "";
          const scoreLabel = typeof entry.score === "number" ? `  ${entry.score}` : "";
          const flagsLabel = entry.scoreFlags?.length ? ` [${entry.scoreFlags.join(", ")}]` : "";
          const originLabel = entry.origin ? ` (origin: ${entry.origin})` : "";
          // DEC-446 / TEN-2383: same tier label as orient-shared.ts formatGovernanceEntry
          // tier 1=[direct], tier 2=[domain], tier 3=[org]. Keep in sync with orient-shared.ts.
          const tierLabel = entry.tier === 1 ? " `[direct]`" : entry.tier === 2 ? " `[domain]`" : entry.tier === 3 ? " `[org]`" : "";
          lines.push(`- ${id}${entry.name}${tierLabel}${scoreLabel}${flagsLabel}${originLabel}`);
          if (entry.preview) lines.push(`  _${entry.preview}_`);
        }
      };

      const visibleCollectionBuckets = bindingGovernanceCollections.filter((bucket) => bucket.entries.length > 0);
      if (visibleCollectionBuckets.length > 0) {
        for (const bucket of visibleCollectionBuckets) {
          const title = bucket.collectionName ?? bucket.collectionSlug ?? "Binding governance";
          lines.push(`### ${title} (${bucket.entries.length})`);
          renderGovernanceEntries(bucket.entries);
          lines.push("");
        }
      } else {
        const governanceSections: Array<[string, BindingGovernanceEntry[]]> = [
          ["Principles", result.bindingGovernance.principles],
          ["Standards", result.bindingGovernance.standards],
          ["Business Rules", result.bindingGovernance.businessRules],
        ];

        for (const [title, entries] of governanceSections) {
          if (entries.length === 0) continue;
          lines.push(`### ${title} (${entries.length})`);
          renderGovernanceEntries(entries);
          lines.push("");
        }
      }
    }

    lines.push("## Steering context");
    lines.push("_These entries set the frame for the work before you drop into broader supporting detail._");
    lines.push("");
    if (steeringCount === 0) {
      lines.push("_No steering context matched yet._");
      lines.push("");
    } else {
      for (const entry of result.steeringContext) {
        const id = entry.entryId ? `**${entry.entryId}:** ` : "";
        const scoreLabel = typeof entry.score === "number" ? `  ${entry.score}` : "";
        const flagsLabel = entry.scoreFlags?.length ? ` [${entry.scoreFlags.join(", ")}]` : "";
        const originLabel = entry.origin ? ` (origin: ${entry.origin})` : "";
        const hopLabel = entry.hop > 0 ? ` _(hop ${entry.hop}, ${entry.relationDirection === "outgoing" ? "→" : "←"} ${entry.relationType})_` : "";
        lines.push(`- ${id}${entry.name}${scoreLabel}${flagsLabel}${originLabel}${hopLabel}`);
      }
      lines.push("");
    }

    lines.push("## Supporting context");
    lines.push("_These entries add implementation detail, history, and neighboring domain context._");
    lines.push("");

    if (byCollection.size === 0) {
      lines.push("_No supporting context matched yet. Open the binding governance entries first._");
      lines.push("");
    } else {
      for (const [collName, entries] of byCollection) {
        lines.push(`### ${collName} (${entries.length})${epistemicCollectionHint(collName)}`);
        for (const e of entries) {
          const arrow = e.relationDirection === "outgoing" ? "→" : "←";
          const id = e.entryId ? `**${e.entryId}:** ` : "";
          const scoreLabel = typeof e.score === "number" ? `  ${e.score}` : "";
          const flagsLabel = e.scoreFlags?.length ? ` [${e.scoreFlags.join(", ")}]` : "";
          const originLabel = e.origin ? ` (origin: ${e.origin})` : "";
          const hopLabel = e.hop > 0 ? ` _(hop ${e.hop}, ${arrow} ${e.relationType})_` : "";
          lines.push(`- ${id}${e.name}${scoreLabel}${flagsLabel}${originLabel}${hopLabel}`);
        }
        lines.push("");
      }
    }

    lines.push(`_Use \`entries action=get\` for full details on any entry._`);

    const taskEntries = [...result.steeringContext, ...result.supportingContext].map((e) => ({
      entryId: e.entryId ?? String(Math.random()),
      name: e.name,
      collectionName: e.collectionName,
    }));

    return {
      content: [{ type: "text" as const, text: lines.join("\n") }],
      structuredContent: success(
        `Loaded ${result.totalFound} task constraints and related entries (${result.confidence} confidence, ${strategy} strategy).`,
        {
          entries: taskEntries,
          totalFound: result.totalFound,
          confidence: result.confidence,
          groundingStatus: result.groundingStatus,
          strategy,
          bindingGovernance: result.bindingGovernance,
          steeringContext: result.steeringContext.map((entry) => ({
            entryId: entry.entryId ?? null,
            name: entry.name,
            collectionName: entry.collectionName,
            relationType: entry.relationType,
            relationDirection: entry.relationDirection,
            hop: entry.hop,
            score: entry.score,
            scoreFlags: entry.scoreFlags,
            preview: entry.preview,
            origin: entry.origin,
          })),
          supportingContext: result.supportingContext.map((entry) => ({
            entryId: entry.entryId ?? null,
            name: entry.name,
            collectionName: entry.collectionName,
            relationType: entry.relationType,
            relationDirection: entry.relationDirection,
            hop: entry.hop,
            score: entry.score,
            scoreFlags: entry.scoreFlags,
            preview: entry.preview,
            origin: entry.origin,
          })),
        },
      ),
    };
  }

  // Entry mode: traverse the knowledge graph around a specific entry
  await server.sendLoggingMessage({
    level: "info",
    data: `Gathering context for ${entryId} (depth: ${maxHops})...`,
    logger: "product-brain",
  });

  const result = await kernelQuery<GatherResult>("chain.gatherContext", { entryId, maxHops });

  if (!result?.root) {
    return notFoundResult(entryId!, `Entry '${entryId}' not found. Try search to find the right ID.`);
  }
  const root = result.root;

  if (result.related.length === 0) {
    const recorded = recordGap({ query: root.entryId, tool: "context", action: "gather", gapType: "context_entry_isolated" });
    if (recorded) {
      getWorkspaceId()
        .then((wsId) => trackKnowledgeGap(wsId, { query: root.entryId, tool: "context", action: "gather", gap_type: "context_entry_isolated" }))
        .catch(() => {});
    }

    return {
      content: [{
        type: "text" as const,
        text: `# Context for ${root.entryId}: ${root.name}\n\n` +
          `_No relations found._ This entry is not yet connected to the knowledge graph.\n\n` +
          `Use \`relations action=suggest\` to discover potential connections, or \`relations action=create\` to link manually.`,
      }],
      structuredContent: success(
        `No relations found for ${root.entryId}. Entry not yet connected.`,
        { root, related: [], totalRelations: 0 },
        [{ tool: "relations", description: "Discover connections", parameters: { action: "suggest", entryId: root.entryId } }],
      ),
    };
  }

  const byCollection = new Map<string, typeof result.related>();
  for (const entry of result.related) {
    const key = entry.collectionName;
    if (!byCollection.has(key)) byCollection.set(key, []);
    byCollection.get(key)!.push(entry);
  }

  const lines: string[] = [
    `# Context for ${root.entryId}: ${root.name}`,
    `_${result.totalRelations} related entries across ${byCollection.size} collections (${result.hopsTraversed} hops traversed)_`,
    "",
  ];

  for (const [collName, entries] of byCollection) {
    lines.push(`## ${collName} (${entries.length})${epistemicCollectionHint(collName)}`);
    for (const e of entries) {
      const arrow = e.relationDirection === "outgoing" ? "\u2192" : "\u2190";
      const hopLabel = e.hop > 1 ? ` (hop ${e.hop})` : "";
      const id = e.entryId ? `${e.entryId}: ` : "";
      lines.push(`- ${arrow} **${e.relationType}** ${id}${e.name}${hopLabel}`);
    }
    lines.push("");
  }

  const rootId = root.entryId ?? "root";
  const nodes = [
    { id: rootId, name: root.name, collectionName: "" },
    ...result.related.map((e) => ({
      id: e.entryId ?? String(Math.random()),
      name: e.name,
      collectionName: e.collectionName,
    })),
  ];
  const edges = result.related
    .filter((e) => e.entryId)
    .map((e) => ({
      source: rootId,
      target: e.entryId!,
      type: e.relationType,
    }));

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Gathered ${result.totalRelations} related entries for ${root.entryId} (${result.hopsTraversed} hops).`,
      { nodes, edges, root, totalRelations: result.totalRelations },
    ),
  };
}

async function handleJourneyGather(
  server: McpServer,
  mapEntryId: string,
  maxHops: number,
  maxResults: number,
) {
  await server.sendLoggingMessage({
    level: "info",
    data: `Loading journey context for map ${mapEntryId}...`,
    logger: "product-brain",
  });

  const result = await kernelQuery<JourneyGatherResult | null>("chain.journeyAwareGatherContext", {
    mapEntryId,
    maxHops,
    maxResults,
  });

  if (!result) {
    return notFoundResult(
      mapEntryId,
      `Journey map '${mapEntryId}' not found or has no slot data. Verify the map entry ID.`,
      "Verify the map entry ID is correct.",
    );
  }

  if (result.stages.length === 0 || result.seedCount === 0) {
    return {
      content: [{
        type: "text" as const,
        text: `# Journey Context: ${result.map.name}\n\n` +
          `_No ingredients found in this journey map._\n\n` +
          `Add journey steps via MCP: \`map action=slot.add mapEntryId="${mapEntryId}" slotId="..." ingredientEntryId="..."\``,
      }],
      structuredContent: success(
        `Journey map ${result.map.name} has no ingredients.`,
        { map: result.map, stages: result.stages, context: [] },
      ),
    };
  }

  const lines: string[] = [
    `# Journey Context: ${result.map.name}`,
    `**Template:** ${result.map.templateName}  |  **Map ID:** ${result.map.entryId}`,
    `**Steps scanned:** ${result.seedCount}${result.truncated ? ` (capped at 10 — ${result.stages.flatMap((s) => s.ingredients).length} total ingredients)` : ""}`,
    `**Related knowledge:** ${result.totalFound} entries`,
    "",
    "## Journey Stages",
  ];

  for (const stage of result.stages) {
    const ingredientList = stage.ingredients.length === 0
      ? "_empty_"
      : stage.ingredients.map((i) => `${i.entryId}: ${i.name}`).join(", ");
    lines.push(`- **${stage.label}**: ${ingredientList}`);
  }

  if (result.context.length > 0) {
    lines.push("");
    lines.push("## Governance & Intelligence");
    lines.push(`_Entries from the knowledge graph connected to journey steps:_`);
    lines.push("");

    const byCollection = new Map<string, ContextEntry[]>();
    for (const entry of result.context) {
      const key = entry.collectionName;
      if (!byCollection.has(key)) byCollection.set(key, []);
      byCollection.get(key)!.push(entry);
    }

    for (const [collName, entries] of byCollection) {
      lines.push(`### ${collName} (${entries.length})`);
      for (const e of entries) {
        const arrow = e.relationDirection === "outgoing" ? "→" : "←";
        const id = e.entryId ? `**${e.entryId}:** ` : "";
        const scoreLabel = typeof e.score === "number" ? `  ${e.score}` : "";
        lines.push(`- ${arrow} ${id}${e.name}${scoreLabel}`);
      }
      lines.push("");
    }
  }

  lines.push(`_Use \`entries action=get\` for full details. Link governance to steps: \`relations action=create\`._`);

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Journey context for ${result.map.name}: ${result.totalFound} related entries across ${result.stages.length} stages.`,
      {
        map: result.map,
        stages: result.stages,
        context: result.context.map((e) => ({
          entryId: e.entryId ?? "",
          name: e.name,
          collectionName: e.collectionName,
        })),
      },
    ),
  };
}

async function handleNeighborhood(entryId: string) {
  const result = await kernelQuery<NeighborhoodResult>("chain.getEntryNeighborhood", { entryId, now: Date.now() });

  const lines: string[] = [`# Neighborhood: ${entryId}`, ""];

  if (result.blockingChain.length > 0) {
    lines.push(`## Blocking Chain (${result.blockingChain.length})`);
    lines.push(`_Entries this one blocks, downstream up to 3 hops:_`);
    for (const item of result.blockingChain) {
      const id = item.entryId ? `**${item.entryId}:** ` : "";
      lines.push(`- ${id}${item.name} [status: ${item.status ?? "unknown"}, hop ${item.hop}]`);
    }
    lines.push("");
  }

  if (result.dependencyChain.length > 0) {
    lines.push(`## Dependency Chain (${result.dependencyChain.length})`);
    lines.push(`_Entries this one depends on or is blocked by:_`);
    for (const item of result.dependencyChain) {
      const id = item.entryId ? `**${item.entryId}:** ` : "";
      const unresolved = item.unresolvedFlag ? " ⚠ unresolved" : "";
      lines.push(`- ${id}${item.name} [${item.relationType ?? ""}${unresolved}]`);
    }
    lines.push("");
  }

  if (result.parentContext) {
    const p = result.parentContext;
    const id = p.entryId ? `**${p.entryId}:** ` : "";
    lines.push(`## Parent Context`);
    lines.push(`- ${id}${p.name} — ${p.openCount} open, ${p.blockedCount} blocked sibling(s)`);
    lines.push("");
  }

  if (result.relatedTensions.length > 0) {
    lines.push(`## Related Tensions (${result.relatedTensions.length})`);
    for (const item of result.relatedTensions) {
      const id = item.entryId ? `**${item.entryId}:** ` : "";
      lines.push(`- ${id}${item.name} [${item.relationType ?? ""}]`);
    }
    lines.push("");
  }

  const signals: string[] = [];
  if (result.isolationSignal) signals.push("⚠ Isolated (< 2 relations)");
  if (result.stalenessSignal?.isStale) {
    signals.push(`⚠ Stale (${result.stalenessSignal.daysSinceUpdate} days since update)`);
  }
  if (signals.length > 0) {
    lines.push(`## Signals`);
    for (const s of signals) lines.push(`- ${s}`);
    lines.push("");
  }

  const isEmpty = result.blockingChain.length === 0 && result.dependencyChain.length === 0
    && !result.parentContext && result.relatedTensions.length === 0;

  if (isEmpty) {
    lines.push(`_No neighborhood data found. Entry may not exist or has no graph connections._`);
    lines.push(`Use \`relations action=suggest\` to discover potential connections.`);
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Neighborhood for ${entryId}: ${result.blockingChain.length} blocking, ${result.dependencyChain.length} dependencies, ${result.relatedTensions.length} tensions.`,
      result,
      isEmpty ? [{ tool: "relations", description: "Discover connections", parameters: { action: "suggest", entryId } }] : undefined,
    ),
  };
}

async function handleBuild(server: McpServer, entryId: string, maxHops: number) {
  requireActiveSession();

  await server.sendLoggingMessage({
    level: "info",
    data: `Assembling build context for ${entryId}...`,
    logger: "product-brain",
  });

  const result = await kernelQuery<BuildResult>("chain.assembleBuildContext", {
    entryId,
    maxHops,
  });

  const { entry, relatedByCollection, businessRules, glossaryTerms, chainRefs, contextTruncated } = result;

  const lines: string[] = [
    `# Build Context: ${entry.name}`,
    `**Entry:** ${entry.entryId ?? entry.name} [${entry.collectionSlug}]`,
    `**Status:** ${entry.status}`,
    "",
  ];

  if (Object.keys(entry.data).length > 0) {
    lines.push("## Entry Data");
    for (const [key, val] of Object.entries(entry.data)) {
      const str = typeof val === "string" ? val : JSON.stringify(val);
      if (str.length > 200) {
        lines.push(`- **${key}:** ${str.slice(0, 200)}...`);
      } else {
        lines.push(`- **${key}:** ${str}`);
      }
    }
    lines.push("");
  }

  if (businessRules.length > 0) {
    lines.push("## Business Rules to Honor");
    for (const r of businessRules) {
      const id = r.entryId ?? r.name;
      lines.push(`- **${id}:** ${r.name}`);
      if (r.description) lines.push(`  ${r.description.slice(0, 150)}${r.description.length > 150 ? "..." : ""}`);
    }
    lines.push("");
  }

  if (glossaryTerms.length > 0) {
    lines.push("## Glossary Terms");
    for (const t of glossaryTerms.slice(0, 15)) {
      const id = t.entryId ?? t.name;
      lines.push(`- **${id}:** ${t.name}`);
      if (t.definition) lines.push(`  ${t.definition.slice(0, 120)}${t.definition.length > 120 ? "..." : ""}`);
    }
    if (glossaryTerms.length > 15) lines.push(`  _...and ${glossaryTerms.length - 15} more_`);
    lines.push("");
  }

  const relatedCount = Object.values(relatedByCollection).reduce((sum, arr) => sum + arr.length, 0);
  if (relatedCount > 0) {
    lines.push("## Related Entries");
    for (const [coll, entries] of Object.entries(relatedByCollection)) {
      lines.push(`### ${coll} (${entries.length})`);
      for (const e of entries.slice(0, 5)) {
        lines.push(`- ${e.entryId ?? e.name}: ${e.name} [${e.relationType}]`);
      }
      if (entries.length > 5) lines.push(`  _...and ${entries.length - 5} more_`);
    }
    lines.push("");
  }

  lines.push("## Chain Refs to Consult");
  lines.push(chainRefs.slice(0, 15).map((r: string) => `- ${r}`).join("\n"));

  if (contextTruncated) {
    lines.push("");
    lines.push("_Context was truncated (limits: 50 related, 20 rules, 30 glossary). Use entries action=get for full details._");
  }

  return {
    content: [{ type: "text" as const, text: annotateStaleNames(lines.join("\n")) }],
    structuredContent: success(
      `Build context for ${entry.name}: ${relatedCount} related, ${businessRules.length} rules, ${glossaryTerms.length} terms.`,
      { entry, relatedByCollection, businessRulesCount: businessRules.length, glossaryTermsCount: glossaryTerms.length, contextTruncated },
    ),
  };
}

// ─── Cross-Cut (BET-239, Slice 3) ───────────────────────────────────────────

interface StructuralAggregationItem {
  entryId: string | null;
  name: string;
  relatedEntryId: string | null;
  relatedName: string;
  relatedCollectionSlug: string;
}

interface StructuralAggregationResult {
  groups: Record<string, StructuralAggregationItem[]>;
  totalCount: number;
  truncated: boolean;
}

async function handleCrossCut(relationType: string) {
  const result = await kernelQuery<StructuralAggregationResult>("chain.structuralAggregation", {
    relationType,
  });

  const groupKeys = Object.keys(result.groups);

  if (result.totalCount === 0) {
    return {
      content: [{
        type: "text" as const,
        text: `# Cross-Cut: ${relationType}\n\nNo relations of type '${relationType}' found in this workspace.`,
      }],
      structuredContent: success(
        `No '${relationType}' relations found.`,
        { groups: {}, totalCount: 0, truncated: false },
      ),
    };
  }

  const lines: string[] = [
    `# Cross-Cut: ${relationType}`,
    `_${result.totalCount} relations across ${groupKeys.length} source collection${groupKeys.length === 1 ? "" : "s"}${result.truncated ? " (TRUNCATED — more relations exist)" : ""}_`,
    "",
  ];

  for (const [sourceSlug, items] of Object.entries(result.groups)) {
    lines.push(`## ${sourceSlug} (${items.length})`);
    for (const item of items) {
      const fromId = item.entryId ? `**${item.entryId}:** ` : "";
      const toId = item.relatedEntryId ?? item.relatedName;
      lines.push(`- ${fromId}${item.name} → ${toId} [${item.relatedCollectionSlug}]`);
    }
    lines.push("");
  }

  if (result.truncated) {
    lines.push("_Results truncated at 500 relations. Filter by collection or use a more specific relation type._");
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `${result.totalCount} '${relationType}' relations across ${groupKeys.length} collections.${result.truncated ? " Truncated." : ""}`,
      result,
    ),
  };
}

// ─── Deep Chain Walk (BET-239, Slice 2) ────────────────────────────────────────

interface ChainWalkNode {
  entryId: string | null;
  name: string;
  collectionSlug: string | null;
  depth: number;
  parentEntryId: string | null;
}

interface ChainWalkEdge {
  from: string;
  to: string;
  type: string;
}

interface ChainWalkCycle {
  path: string[];
}

interface ChainWalkResult {
  nodes: ChainWalkNode[];
  edges: ChainWalkEdge[];
  cycles: ChainWalkCycle[];
  totalNodes: number;
  truncated: boolean;
  snapshotWarning: boolean;
  error?: string;
}

async function handleChainWalk(
  entryId: string,
  direction: "outgoing" | "incoming",
  maxHops: number,
  relationType?: string,
) {
  // Clamp maxHops for chain action to 1-4
  const clampedHops = Math.max(1, Math.min(4, maxHops));

  const result = await kernelQuery<ChainWalkResult>("chain.deepChainWalk", {
    entryId,
    direction,
    maxHops: clampedHops,
    ...(relationType ? { relationType } : {}),
  });

  if (result.error) {
    return notFoundResult(entryId, result.error);
  }

  if (result.totalNodes <= 1) {
    return {
      content: [{
        type: "text" as const,
        text: `# Chain Walk: ${entryId}\n\n` +
          `_No ${direction} relations found${relationType ? ` of type '${relationType}'` : ""}._\n\n` +
          `Try \`context action=chain entryId="${entryId}" direction="${direction === "outgoing" ? "incoming" : "outgoing"}"\` ` +
          `or remove the relationType filter.`,
      }],
      structuredContent: success(
        `No ${direction} relations found for ${entryId}.`,
        result,
      ),
    };
  }

  const lines: string[] = [
    `# Chain Walk: ${entryId}`,
    `**Direction:** ${direction}  |  **Depth:** ${clampedHops}${relationType ? `  |  **Type filter:** ${relationType}` : ""}`,
    `**Nodes:** ${result.totalNodes}${result.truncated ? " (TRUNCATED at 100)" : ""}  |  **Edges:** ${result.edges.length}  |  **Cycles:** ${result.cycles.length}`,
    "",
  ];

  // Render as tree grouped by depth
  const maxDepth = Math.max(...result.nodes.map((n) => n.depth));
  for (let d = 0; d <= maxDepth; d++) {
    const atDepth = result.nodes.filter((n) => n.depth === d);
    if (atDepth.length === 0) continue;

    const label = d === 0 ? "Root" : `Depth ${d}`;
    lines.push(`## ${label} (${atDepth.length})`);
    for (const node of atDepth) {
      const indent = "  ".repeat(d);
      const id = node.entryId ? `**${node.entryId}:** ` : "";
      const collection = node.collectionSlug ? ` [${node.collectionSlug}]` : "";
      const parent = node.parentEntryId ? ` (via ${node.parentEntryId})` : "";
      lines.push(`${indent}- ${id}${node.name}${collection}${parent}`);
    }
    lines.push("");
  }

  // Show cycles if any
  if (result.cycles.length > 0) {
    lines.push("## Cycles Detected");
    for (const cycle of result.cycles) {
      lines.push(`- ${cycle.path.join(" -> ")}`);
    }
    lines.push("");
  }

  // Snapshot warning
  if (result.snapshotWarning) {
    lines.push("_Note: This traversal spans multiple reads (Convex action). Data consistency is not guaranteed across hops (TEN-1029)._");
  }

  if (result.truncated) {
    lines.push("_Results truncated at 100 nodes. Narrow with relationType filter or reduce maxHops._");
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Chain walk for ${entryId}: ${result.totalNodes} nodes, ${result.edges.length} edges, ${result.cycles.length} cycles (${direction}, depth ${clampedHops}).`,
      result,
    ),
  };
}

// ─── Changes (BET-239, FEAT-778) ────────────────────────────────────────────

interface ChangeDetectionResult {
  entries: Array<{ entryId: string | null; name: string; collectionSlug: string; updatedAt: number }>;
  relations: Array<{ fromEntryId: string | null; toEntryId: string | null; type: string; createdAt: number }>;
  counts: Record<string, number>;
  truncated: boolean;
}

async function handleChanges(since: string) {
  const sinceMs = Date.parse(since);
  if (isNaN(sinceMs)) {
    return validationResult(`Invalid 'since' timestamp: '${since}'. Use ISO 8601 format (e.g. '2026-03-24T00:00:00Z').`);
  }

  const result = await kernelQuery<ChangeDetectionResult>("chain.changeDetection", { since: sinceMs });

  const totalEntries = result.entries.length;
  const totalRelations = result.relations.length;

  if (totalEntries === 0 && totalRelations === 0) {
    return {
      content: [{
        type: "text" as const,
        text: `# Changes since ${since}\n\nNo entries modified or relations created since this timestamp.`,
      }],
      structuredContent: success(
        `No changes detected since ${since}.`,
        { entries: [], relations: [], counts: {}, truncated: false },
      ),
    };
  }

  const lines: string[] = [
    `# Changes since ${since}`,
    `_${totalEntries} entries modified, ${totalRelations} relations created${result.truncated ? " (TRUNCATED — more changes exist)" : ""}_`,
    "",
  ];

  // Entries grouped by collection
  if (totalEntries > 0) {
    lines.push("## Modified Entries");
    const sortedCounts = Object.entries(result.counts).sort((a, b) => b[1] - a[1]);
    for (const [slug, count] of sortedCounts) {
      lines.push(`### ${slug} (${count})`);
      const slugEntries = result.entries.filter((e) => e.collectionSlug === slug);
      for (const e of slugEntries) {
        const id = e.entryId ? `**${e.entryId}:** ` : "";
        const ago = formatTimeAgo(e.updatedAt);
        lines.push(`- ${id}${e.name} _(${ago})_`);
      }
      lines.push("");
    }
  }

  // Relations
  if (totalRelations > 0) {
    lines.push("## New Relations");
    for (const r of result.relations) {
      const from = r.fromEntryId ?? "?";
      const to = r.toEntryId ?? "?";
      const ago = formatTimeAgo(r.createdAt);
      lines.push(`- ${from} **${r.type}** ${to} _(${ago})_`);
    }
    lines.push("");
  }

  if (result.truncated) {
    lines.push("_Results truncated at 200 entries. Use a more recent timestamp to narrow the window._");
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `${totalEntries} entries modified, ${totalRelations} relations created since ${since}.${result.truncated ? " Truncated." : ""}`,
      result,
    ),
  };
}

// ─── Incremental (BET-239 E4) ────────────────────────────────────────────

interface IncrementalResult {
  newEntries: Array<{ entryId: string | null; name: string; collectionSlug: string; updatedAt: number }>;
  counts: Record<string, number>;
  relations: Array<{ fromEntryId: string | null; toEntryId: string | null; type: string; createdAt: number }>;
  truncated: boolean;
  previousRunTimestamp: number | null;
  isFirstRun: boolean;
}

async function handleIncremental(skill: string) {
  const result = await kernelQuery<IncrementalResult>("chain.incrementalChanges", { skill });

  const totalEntries = result.newEntries.length;

  // Record the brief run so the next call sees an updated "since last brief" timestamp.
  // Fire-and-forget: recording failure should not block the response.
  const surfacedIds = result.newEntries
    .map((e) => e.entryId)
    .filter((id): id is string => id !== null);
  kernelMutation("chain.recordBriefRun", {
    skill,
    entriesSurfaced: surfacedIds,
    timestamp: Date.now(),
  }).catch(() => {
    // Best-effort — logging omitted to avoid noise. The next brief run
    // will simply re-surface the same entries.
  });

  if (totalEntries === 0 && !result.isFirstRun) {
    return {
      content: [{
        type: "text" as const,
        text: `# Incremental Brief — ${skill}\n\nNo new entries since last brief run (${formatTimeAgo(result.previousRunTimestamp!)}).`,
      }],
      structuredContent: success(
        `No new entries for skill '${skill}' since last brief.`,
        result,
      ),
    };
  }

  const lines: string[] = [
    `# Incremental Brief — ${skill}`,
  ];

  if (result.isFirstRun) {
    lines.push("_First run — showing all recent entries._");
  } else {
    lines.push(`_Delta since last brief (${formatTimeAgo(result.previousRunTimestamp!)})_`);
  }

  lines.push(`_${totalEntries} new entries${result.truncated ? " (TRUNCATED)" : ""}_`);
  lines.push("");

  if (totalEntries > 0) {
    lines.push("## New Entries");

    // Group by collection
    const byCollection: Record<string, typeof result.newEntries> = {};
    for (const e of result.newEntries) {
      const slug = e.collectionSlug;
      if (!byCollection[slug]) byCollection[slug] = [];
      byCollection[slug].push(e);
    }

    const sortedSlugs = Object.entries(byCollection)
      .sort((a, b) => b[1].length - a[1].length)
      .map(([slug]) => slug);

    for (const slug of sortedSlugs) {
      const entries = byCollection[slug];
      lines.push(`### ${slug} (${entries.length})`);
      for (const e of entries) {
        const id = e.entryId ? `**${e.entryId}:** ` : "";
        const ago = formatTimeAgo(e.updatedAt);
        lines.push(`- ${id}${e.name} _(${ago})_`);
      }
      lines.push("");
    }
  }

  if (result.truncated) {
    lines.push("_Results truncated at 200 entries. Run more frequently to stay within the cap._");
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `${totalEntries} new entries for skill '${skill}'${result.isFirstRun ? " (first run)" : ""}.${result.truncated ? " Truncated." : ""}`,
      result,
    ),
  };
}

// ─── Compound Brief (BET-239 E6) ────────────────────────────────────────────

async function handleBrief(briefType: "steering" | "confidence" | "delta", sinceMs?: number) {
  const args: Record<string, unknown> = { type: briefType };
  if (sinceMs !== undefined) args.since = sinceMs;

  const result = await kernelQuery<any>("chain.compoundQuery", args);

  if (briefType === "steering") {
    return formatSteeringBrief(result);
  }

  if (briefType === "confidence") {
    return formatConfidencePass(result);
  }

  if (briefType === "delta") {
    return formatDeltaSync(result);
  }

  return validationResult(`Unknown briefType: ${briefType}`);
}

function formatSteeringBrief(result: any) {
  const changes = result.changes;
  const delta = result.delta;
  const readiness = result.readiness;

  const lines: string[] = [
    "# Steering Brief",
    "",
    `## Changes (last 7 days)`,
    `_${changes.entries.length} entries modified, ${changes.relations.length} relations created${changes.truncated ? " (TRUNCATED)" : ""}_`,
    "",
  ];

  // Entry counts by collection
  if (Object.keys(changes.counts).length > 0) {
    const sortedCounts = Object.entries(changes.counts as Record<string, number>).sort((a, b) => b[1] - a[1]);
    for (const [slug, count] of sortedCounts) {
      lines.push(`- **${slug}:** ${count}`);
    }
    lines.push("");
  }

  // Structural summary
  lines.push("## Structure");
  for (const [relType, agg] of Object.entries(result.structure as Record<string, any>)) {
    const groupCount = Object.keys(agg.groups).length;
    lines.push(`- **${relType}:** ${agg.totalCount} relations across ${groupCount} collection groups${agg.truncated ? " (TRUNCATED)" : ""}`);
  }
  lines.push("");

  // Delta
  lines.push("## Incremental Delta (steering skill)");
  if (delta.isFirstRun) {
    lines.push("_First run — all recent entries included._");
  }
  lines.push(`${delta.newEntries.length} new entries since last steering brief${delta.truncated ? " (TRUNCATED)" : ""}`);
  lines.push("");

  // Readiness
  lines.push("## Workspace Readiness");
  lines.push(`Score: **${readiness.score}%** (${readiness.passedChecks}/${readiness.totalChecks} checks passed)`);
  if (readiness.gaps.length > 0) {
    lines.push("");
    lines.push("**Gaps:**");
    for (const gap of readiness.gaps.slice(0, 5)) {
      lines.push(`- ${gap.label}: ${gap.current}/${gap.required}${gap.guidance ? ` — ${gap.guidance}` : ""}`);
    }
  }
  lines.push("");

  // BET-240 S6: trust-awareness note — scoring kernel applies implicit trust weighting
  lines.push("## Trust");
  lines.push("_Scoring kernel applies trust weighting: human=1.0, agent+verified=0.7, agent+unverified=0.56, external+unverified=0.64. Scores in context/gather reflect this. Use `orient -b` for workspace trust metrics._");
  lines.push("");

  if (result.snapshotWarning) {
    lines.push("_Note: Compound query spans multiple reads. Data consistency is not guaranteed across components (TEN-1029)._");
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Steering brief: ${changes.entries.length} changes, ${delta.newEntries.length} new entries, ${readiness.score}% readiness.`,
      { ...result, trustFilterActive: true },
    ),
  };
}

function formatConfidencePass(result: any) {
  const changes = result.changes;

  const lines: string[] = [
    "# Confidence Pass",
    "",
    `## Changes (last 7 days)`,
    `_${changes.entries.length} entries modified${changes.truncated ? " (TRUNCATED)" : ""}_`,
    "",
  ];

  // Active bets
  lines.push(`## Active Bets (${result.betCount})`);
  if (result.activeBets.length > 0) {
    for (const bet of result.activeBets) {
      const id = bet.entryId ? `**${bet.entryId}:** ` : "";
      lines.push(`- ${id}${bet.name} [${bet.status}]`);
    }
  } else {
    lines.push("_No active bets._");
  }
  lines.push("");

  // Active tensions
  lines.push(`## Active Tensions (${result.tensionCount})`);
  if (result.activeTensions.length > 0) {
    for (const tension of result.activeTensions) {
      const id = tension.entryId ? `**${tension.entryId}:** ` : "";
      lines.push(`- ${id}${tension.name}`);
    }
  } else {
    lines.push("_No active tensions._");
  }
  lines.push("");

  // BET-240 S6: trust-awareness note for confidence pass
  lines.push("## Trust");
  lines.push("_Trust-weighted scoring active: agent-origin and unverified entries receive lower confidence scores (human=1.0, agent+verified=0.7, agent+unverified=0.56). Use `entries action=get` to inspect individual entry origin/verification. Use `orient -b` for workspace trust metrics._");
  lines.push("");

  if (result.snapshotWarning) {
    lines.push("_Note: Compound query spans multiple reads. Data consistency is not guaranteed across components (TEN-1029)._");
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Confidence pass: ${changes.entries.length} changes, ${result.betCount} active bets, ${result.tensionCount} active tensions.`,
      { ...result, trustFilterActive: true },
    ),
  };
}

function formatDeltaSync(result: any) {
  const changes = result.changes;
  const sinceDate = new Date(result.since).toISOString();

  const lines: string[] = [
    `# Delta Sync since ${sinceDate}`,
    `_${changes.entries.length} entries modified, ${changes.relations.length} relations created${changes.truncated ? " (TRUNCATED)" : ""}_`,
    "",
  ];

  if (changes.entries.length > 0) {
    lines.push("## Modified Entries");
    const sortedCounts = Object.entries(changes.counts as Record<string, number>).sort((a, b) => b[1] - a[1]);
    for (const [slug, count] of sortedCounts) {
      lines.push(`### ${slug} (${count})`);
      const slugEntries = changes.entries.filter((e: any) => e.collectionSlug === slug);
      for (const e of slugEntries) {
        const id = e.entryId ? `**${e.entryId}:** ` : "";
        const ago = formatTimeAgo(e.updatedAt);
        lines.push(`- ${id}${e.name} _(${ago})_`);
      }
      lines.push("");
    }
  }

  if (changes.relations.length > 0) {
    lines.push("## New Relations");
    for (const r of changes.relations) {
      const from = r.fromEntryId ?? "?";
      const to = r.toEntryId ?? "?";
      const ago = formatTimeAgo(r.createdAt);
      lines.push(`- ${from} **${r.type}** ${to} _(${ago})_`);
    }
    lines.push("");
  }

  if (result.snapshotWarning) {
    lines.push("_Note: Compound query spans multiple reads. Data consistency is not guaranteed across components (TEN-1029)._");
  }

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Delta sync since ${sinceDate}: ${changes.entries.length} entries, ${changes.relations.length} relations.${changes.truncated ? " Truncated." : ""}`,
      result,
    ),
  };
}

/** Format a timestamp as a human-readable relative time. */
function formatTimeAgo(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}
